/**
 * Página de detalle de compra
 * 
 * Muestra información detallada de una compra y permite
 * cambiar su estado.
 * 
 * @module pages/compras/DetalleCompra
 * @requires react, react-router-dom, ../../services/compras.service
 * @related_files ./Compras.js
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Servicios
import comprasService from '../../services/compras.service';
import { formatCompraFecha, referenciaCompra, importeRenglonCompra } from '../../utils/compraPresentacion';
import { interpretarRecepcion, describirResultado } from '../../utils/recepcionCosto';
import { veCostosCompra, conservaImporteComprobante } from '../../utils/compraPrivacidad';
import { matrixAllows } from '../../utils/matrixAllows';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCompraEstadoLabel,
  getCompraPagoFlujoLabel,
  compraNecesitaFactura,
  isCompraPendiente,
  isCompraRecibida,
  warnDetallesSinProductoId
} from '../../utils/compraEstado';

// Componentes
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import CompraCostosCambioModal from '../../components/compras/CompraCostosCambioModal';

// Iconos
import { 
  FaShoppingCart, FaArrowLeft, FaCheck, FaTimes,
  FaUserTie, FaCalendarAlt, FaInfoCircle, FaBoxOpen,
  FaFileAlt, FaPhone, FaEnvelope, FaClock, FaIdCard,
  FaMapMarkerAlt, FaBuilding, FaTag, FaBarcode, FaUndo
} from 'react-icons/fa';

/**
 * Componente de página para detalle de compra
 * @returns {JSX.Element} Componente DetalleCompra
 */
const DetalleCompra = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, permisosEfectivos } = useAuth();
  const veCostos = veCostosCompra(permisosEfectivos);
  const muestraImporte = conservaImporteComprobante(permisosEfectivos);
  const puedeRevisarCostos = veCostos && matrixAllows(permisosEfectivos, 'compras', 'editar');

  // Estado
  const [compra, setCompra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accion, setAccion] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [costosModal, setCostosModal] = useState({
    open: false,
    cambios: [],
    applying: false,
    error: '',
    tipoRedondeo: 'sin_redondeo',
    resultado: null,
    alineadas: [],
    bloqueadas: [],
    incidente: null
  });
  
  /**
   * Carga los datos de la compra
   */
  useEffect(() => {
    const cargarCompra = async () => {
      try {
        const data = await comprasService.obtenerPorId(id);
        setCompra(data);
      } catch (error) {
        console.error('Error al cargar compra:', error);
        toast.error('Error al cargar los datos de la compra');
        navigate('/compras');
      } finally {
        setLoading(false);
      }
    };
    
    cargarCompra();
  }, [id, navigate]);
  
  /**
   * Prepara la recepción de la compra
   */
  const prepararRecibirCompra = () => {
    setAccion('recibir');
    setShowConfirmDialog(true);
  };
  
  /**
   * Prepara la cancelación de la compra
   */
  const prepararCancelarCompra = () => {
    setAccion('cancelar');
    setShowConfirmDialog(true);
  };

  /**
   * Prepara la devolución de la compra
   */
  const prepararDevolverCompra = () => {
    setAccion('devolver');
    setShowConfirmDialog(true);
  };
  
  /**
   * Confirma la acción sobre la compra
   */
  const aplicarDecisiones = async (decisiones, tipoRedondeo) => {
    setCostosModal((s) => ({ ...s, applying: true, error: '' }));
    try {
      const data = await comprasService.aplicarDecisionesCosto(id, decisiones, tipoRedondeo);
      const resultado = describirResultado(data, costosModal.cambios);
      if (resultado.parcial) {
        const fresco = await comprasService.consultarCambiosCosto(id, tipoRedondeo);
        const cambios = Array.isArray(fresco?.cambios_costo) ? fresco.cambios_costo : [];
        setCostosModal((s) => ({
          ...s,
          applying: false,
          cambios: cambios.length ? cambios : s.cambios,
          resultado,
          error: 'No se aplicaron todas las decisiones. El stock recibido no se revirtió.'
        }));
        return;
      }
      setCostosModal((s) => ({ ...s, applying: false, resultado, error: '' }));
      toast.success('Decisiones de costo registradas.', { autoClose: 3600 });
    } catch (err) {
      setCostosModal((s) => ({
        ...s,
        applying: false,
        error: err?.response?.data?.message || err?.message || 'No se pudieron registrar las decisiones'
      }));
    }
  };

  const cambiarRedondeo = async (tipo) => {
    try {
      const fresco = await comprasService.consultarCambiosCosto(id, tipo);
      const cambios = Array.isArray(fresco?.cambios_costo) ? fresco.cambios_costo : costosModal.cambios;
      setCostosModal((s) => ({ ...s, tipoRedondeo: tipo, cambios }));
    } catch (err) {
      setCostosModal((s) => ({ ...s, error: err?.message || 'No se pudo recalcular el redondeo' }));
    }
  };

  const abrirRevisionCostos = async () => {
    try {
      const fresco = await comprasService.consultarCambiosCosto(id, 'sin_redondeo');
      const cambios = Array.isArray(fresco?.cambios_costo) ? fresco.cambios_costo : [];
      const alineadas = Array.isArray(fresco?.revisiones_sin_diferencia) ? fresco.revisiones_sin_diferencia : [];
      const bloqueadas = Array.isArray(fresco?.revisiones_bloqueadas) ? fresco.revisiones_bloqueadas : [];
      const incidente = fresco?.incidente || null;
      if (!cambios.length && !alineadas.length && !bloqueadas.length && !incidente) {
        toast.success('No hay diferencias de costo para revisar.');
        return;
      }
      setCostosModal({
        open: true,
        cambios,
        applying: false,
        error: '',
        tipoRedondeo: 'sin_redondeo',
        resultado: null,
        alineadas,
        bloqueadas,
        incidente
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'No se pudieron consultar los costos');
    }
  };

  const cerrarSinCambios = async (productoIds) => {
    try {
      setCostosModal((s) => ({ ...s, applying: true, error: '' }));
      const data = await comprasService.cerrarRevisionesSinCambios(id, productoIds);
      const resultado = describirResultado(data, costosModal.cambios);
      setCostosModal((s) => ({
        ...s,
        applying: false,
        resultado,
        alineadas: resultado.parcial ? s.alineadas : [],
        error: resultado.parcial ? 'Alguna revisión no se cerró.' : ''
      }));
      if (!resultado.parcial) toast.success('Revisión cerrada sin cambiar costo, precios ni stock.');
    } catch (err) {
      setCostosModal((s) => ({ ...s, applying: false, error: err?.response?.data?.message || err?.message || 'No se pudo cerrar la revisión' }));
    }
  };

  const recuperarRevision = async () => {
    try {
      setCostosModal((s) => ({ ...s, applying: true, error: '' }));
      const data = await comprasService.recuperarRevisionCosto(id);
      const resultado = describirResultado(data, costosModal.cambios);
      setCostosModal((s) => ({
        ...s,
        applying: false,
        resultado,
        incidente: resultado.parcial ? s.incidente : null,
        error: resultado.parcial ? 'La recuperación quedó incompleta. El stock no se modificó.' : ''
      }));
      if (!resultado.parcial) toast.success('Revisión recuperada. No se aplicó una decisión comercial.');
    } catch (err) {
      setCostosModal((s) => ({ ...s, applying: false, error: err?.response?.data?.message || err?.message || 'No se pudo recuperar la revisión' }));
    }
  };

  const confirmarAccion = async () => {
    try {
      if (accion === 'recibir') {
        warnDetallesSinProductoId(compra?.detalles, id);
        const data = await comprasService.recibirCompra(id, {
          usuario_id: currentUser?.id || currentUser?.email || '',
          detalles: compra?.detalles
        });
        const info = interpretarRecepcion(data);
        if (info.estado && info.estado !== 'recibida') {
          toast.warning('Recepción registrada, pero el servidor no confirmó estado "recibida".');
        } else if (info.revisionPendiente || info.incidente) {
          toast.success(info.mensaje, { autoClose: 5000 });
        } else {
          toast.success(
            info.mensaje
              || (info.procesados != null
                ? `Compra recibida. Stock actualizado (${info.procesados} producto(s)).`
                : 'Compra recibida. Stock actualizado.'),
            { autoClose: 4000 }
          );
        }
        if (info.cambios.length > 0) {
          setCostosModal({ open: true, cambios: info.cambios, applying: false, error: '', tipoRedondeo: 'sin_redondeo', resultado: null });
        }
      } else if (accion === 'cancelar') {
        await comprasService.cancelarCompra(id);
        toast.success('Compra cancelada correctamente');
      } else if (accion === 'devolver') {
        await comprasService.devolverCompra(id, {
          usuario_id: currentUser?.id || currentUser?.email || ''
        });
        toast.success('Compra devuelta correctamente');
      }
      
      // Recargar datos
      const compraActualizada = await comprasService.obtenerPorId(id);
      setCompra(compraActualizada);
    } catch (error) {
      console.error(`Error al ${accion} compra:`, error);
      
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Error al ${accion} la compra`);
      }
    } finally {
      setShowConfirmDialog(false);
      setAccion('');
    }
  };
  
  /**
   * Cancela la acción sobre la compra
   */
  const cancelarAccion = () => {
    setShowConfirmDialog(false);
    setAccion('');
  };
  
  /**
   * Formatea un valor numérico a 2 decimales
   * @param {number} valor - Valor a formatear
   * @returns {string} Valor formateado
   */
  const formatearNumero = (valor) => {
    if (valor === null || valor === undefined || valor === '') {
      return '0.00';
    }
    const numero = parseFloat(valor);
    if (isNaN(numero)) {
      return '0.00';
    }
    return numero.toFixed(2);
  };

  /**
   * Calcula el subtotal de la compra
   * @returns {number} Subtotal calculado
   */
  const calcularSubtotal = () => {
    if (!compra || !compra.detalles) return 0;
    return compra.detalles.reduce((sum, detalle) => {
      const cantidad = parseFloat(detalle.cantidad) || 0;
      const precio = parseFloat(detalle.precio_unitario) || 0;
      return sum + (cantidad * precio);
    }, 0);
  };

  /**
   * Calcula el total de la compra
   * @returns {number} Total calculado
   */
  const calcularTotal = () => {
    const subtotal = calcularSubtotal();
    const impuestos = parseFloat(compra?.impuestos) || 0;
    return subtotal + impuestos;
  };
  
  // Si está cargando, mostrar spinner
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }
  
  // Si no se encontró la compra
  if (!compra) {
    return (
      <Card>
        <div className="text-center py-8">
          <FaShoppingCart className="mx-auto text-4xl text-gray-400 mb-2" />
          <h3 className="text-lg font-medium text-gray-700 mb-1">
            Compra no encontrada
          </h3>
          <p className="text-gray-500 mb-4">
            La compra que intentas ver no existe o ha sido eliminada.
          </p>
          <Button
            color="primary"
            onClick={() => navigate('/compras')}
            icon={<FaArrowLeft />}
          >
            Volver a Compras
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="w-full px-4 lg:px-6 space-y-6">
      {/* Header con información principal */}
      <div className="nexo-surface rounded-lg shadow-sm border border-white/10 p-6">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="bg-indigo-100 p-3 rounded-full">
              <FaShoppingCart className="text-2xl text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Compra · {referenciaCompra(compra)}
              </h1>
              <p className="text-gray-600 mt-1">
                {formatCompraFecha(compra.fecha, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })} - {formatCompraFecha(compra.fecha, {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              color="secondary"
              onClick={() => navigate('/compras')}
              icon={<FaArrowLeft />}
            >
              Volver
            </Button>
            
            {isCompraPendiente(compra.estado) && (
              <>
                <Button
                  color="success"
                  onClick={prepararRecibirCompra}
                  icon={<FaCheck />}
                >
                  Recibir
                </Button>
                
                <Button
                  color="danger"
                  onClick={prepararCancelarCompra}
                  icon={<FaTimes />}
                >
                  Cancelar
                </Button>
              </>
            )}
            
            {puedeRevisarCostos && (compra.estado === 'recibida' || compra.estado === 'completada') && (
              <Button color="secondary" onClick={abrirRevisionCostos}>
                Revisar costos
              </Button>
            )}
            {compra.proveedor_id && (
              <Button
                color="primary"
                onClick={() =>
                  navigate(
                    `/proveedores/${compra.proveedor_id}/cuenta-corriente?tab=acciones`,
                    {
                      state: {
                        compra_id: compra.id,
                        total_sugerido: compra.total,
                        tab: 'acciones',
                      },
                    }
                  )
                }
                icon={<FaFileAlt />}
              >
                {compraNecesitaFactura(compra) ? 'Registrar factura' : 'Cuenta corriente'}
              </Button>
            )}

            {isCompraRecibida(compra.estado) && (
              <Button
                color="warning"
                onClick={prepararDevolverCompra}
                icon={<FaUndo />}
              >
                Devolver
              </Button>
            )}
          </div>
        </div>

        {compraNecesitaFactura(compra) && (
          <div className="mt-4 rounded-lg border border-amber-300/50 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <strong>Siguiente paso:</strong> mercadería recibida. Registrá la factura en la cuenta
            corriente del proveedor para que nazca la deuda (sin factura no hay saldo CxP).
            <button
              type="button"
              className="ml-2 font-semibold underline"
              onClick={() =>
                navigate(
                  `/proveedores/${compra.proveedor_id}/cuenta-corriente?tab=acciones`,
                  {
                    state: {
                      compra_id: compra.id,
                      total_sugerido: compra.total,
                      tab: 'acciones',
                    },
                  }
                )
              }
            >
              Ir a registrar factura
            </button>
          </div>
        )}
        
        {/* Estado de la compra */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">Estado:</span>
              {isCompraPendiente(compra.estado) && (
                <span className="px-4 py-2 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 inline-flex items-center">
                  <FaInfoCircle className="mr-2" />
                  Pendiente de recepción
                </span>
              )}
              {isCompraRecibida(compra.estado) && (
                <span className="px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800 inline-flex items-center">
                  <FaCheck className="mr-2" />
                  {getCompraPagoFlujoLabel(compra)}
                </span>
              )}
              {compra.estado === 'cancelada' && (
                <span className="px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800 inline-flex items-center">
                  <FaTimes className="mr-2" />
                  Cancelada
                </span>
              )}
            </div>
            
            <div className="text-right">
              <div className="text-sm text-gray-500">Total de la Compra</div>
              <div className="text-2xl font-bold text-indigo-600">
                {muestraImporte ? `$${formatearNumero(veCostos ? calcularTotal() : compra.total)}` : 'Reservado'}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Detalle de productos a ancho completo */}
      <Card
        title="Detalle de Productos"
        icon={<FaBoxOpen />}
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
                  Producto
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Código
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Cantidad
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Precio Unit.
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Total
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Categoría
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {compra.detalles.map((detalle, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">
                      {detalle.producto_nombre}
                    </div>
                    {detalle.producto_descripcion && (
                      <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                        {detalle.producto_descripcion}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <FaBarcode className="text-gray-400 mr-1" />
                      <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                        {detalle.producto_codigo || 'Sin código'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center">
                      <FaTag className="text-blue-500 mr-1" />
                      <span className="font-bold text-lg text-blue-600">
                        {detalle.cantidad}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="text-xs text-gray-500">Precio unitario</div>
                    <div className="font-semibold text-green-600">
                      {veCostos ? `$${formatearNumero(detalle.precio_unitario)}` : 'Reservado'}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="text-xs text-gray-500">Total</div>
                    <div className="font-bold text-lg text-indigo-600">
                      {veCostos ? importeRenglonCompra(detalle) : 'Reservado'}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-xs text-gray-500">Categoría</div>
                    <div className="text-sm font-medium bg-gray-100 px-2 py-1 rounded">
                      {detalle.producto_categoria || 'Sin categoría'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Resumen de productos */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Total de Productos</div>
              <div className="font-semibold text-lg">{compra.detalles.length}</div>
            </div>
            <div>
              <div className="text-gray-500">Total de Unidades</div>
              <div className="font-semibold text-lg">
                {compra.detalles.reduce((sum, detalle) => sum + detalle.cantidad, 0)}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Subtotal</div>
              <div className="font-semibold text-lg">{veCostos ? `$${formatearNumero(calcularSubtotal())}` : 'Reservado'}</div>
            </div>
            <div>
              <div className="text-gray-500">Total Final</div>
              <div className="font-bold text-lg text-indigo-600">{muestraImporte ? `$${formatearNumero(veCostos ? calcularTotal() : compra.total)}` : 'Reservado'}</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Bloque inferior con información secundaria */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Panel izquierdo: Datos generales */}
        <div className="xl:col-span-1 space-y-6">
        <Card
            title="Información General"
            icon={<FaInfoCircle />}
          >
            <div className="space-y-4">
              <div className="flex items-center">
                <FaIdCard className="text-gray-500 mr-2" />
                <div>
                  <div className="text-sm text-gray-500">Número de Compra</div>
                  <div className="font-semibold text-lg">{referenciaCompra(compra)}</div>
                </div>
              </div>
              
              <div className="flex items-center">
                <FaCalendarAlt className="text-gray-500 mr-2" />
                <div>
                  <div className="text-sm text-gray-500">Fecha de Creación</div>
                  <div>{formatCompraFecha(compra.fecha, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</div>
                </div>
              </div>
              
              <div className="flex items-center">
                <FaClock className="text-gray-500 mr-2" />
                <div>
                  <div className="text-sm text-gray-500">Hora de Creación</div>
                  <div>{formatCompraFecha(compra.fecha, {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</div>
                </div>
              </div>
              
              <div className="flex items-center">
                <FaUserTie className="text-gray-500 mr-2" />
                <div>
                  <div className="text-sm text-gray-500">Creado por</div>
                  <div className="font-medium">{compra.usuario_nombre} {compra.usuario_apellido}</div>
                  {compra.usuario_email && (
                    <div className="text-xs text-gray-500">{compra.usuario_email}</div>
                  )}
                </div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 mb-1">Estado</div>
                {isCompraPendiente(compra.estado) && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600 inline-flex items-center">
                    <FaInfoCircle className="mr-1" />
                    Pendiente
                  </span>
                )}
                {isCompraRecibida(compra.estado) && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-600 inline-flex items-center">
                    <FaCheck className="mr-1" />
                    {getCompraEstadoLabel(compra.estado)}
                  </span>
                )}
                {compra.estado === 'cancelada' && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-600 inline-flex items-center">
                    <FaTimes className="mr-1" />
                    Cancelada
                  </span>
                )}
              </div>
              
              {compra.fecha_recepcion && (
                <>
                  <div className="flex items-center">
                    <FaCalendarAlt className="text-gray-500 mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Fecha de Recepción</div>
                      <div>{formatCompraFecha(compra.fecha_recepcion, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <FaClock className="text-gray-500 mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Hora de Recepción</div>
                      <div>{formatCompraFecha(compra.fecha_recepcion, {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</div>
                    </div>
                  </div>
                  
                  {compra.usuario_recepcion && (
                    <div className="flex items-center">
                      <FaUserTie className="text-gray-500 mr-2" />
                      <div>
                        <div className="text-sm text-gray-500">Recibido por</div>
                        <div className="font-medium">{compra.usuario_recepcion}</div>
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {compra.notas && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Notas</div>
                  <div className="bg-gray-50 p-2 rounded border border-gray-200 text-sm">
                    {compra.notas}
                  </div>
                </div>
              )}
              
              {compra.fecha_cancelacion && (
                <>
                  <div className="flex items-center">
                    <FaCalendarAlt className="text-red-500 mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Fecha de Cancelación</div>
                      <div className="text-red-600 font-medium">
                        {new Date(compra.fecha_cancelacion).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <FaClock className="text-red-500 mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Hora de Cancelación</div>
                      <div className="text-red-600 font-medium">
                        {new Date(compra.fecha_cancelacion).toLocaleTimeString('es-ES', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {compra.usuario_cancelacion && (
                    <div className="flex items-center">
                      <FaUserTie className="text-red-500 mr-2" />
                      <div>
                        <div className="text-sm text-gray-500">Cancelado por</div>
                        <div className="font-medium text-red-600">{compra.usuario_cancelacion}</div>
                      </div>
                    </div>
                  )}
                  
                  {compra.motivo_cancelacion && (
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Motivo de Cancelación</div>
                      <div className="bg-red-50 p-2 rounded border border-red-200 text-sm text-red-700">
                        {compra.motivo_cancelacion}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
          
          <Card
            title="Información del Proveedor"
            icon={<FaBuilding />}
          >
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">Nombre del Proveedor</div>
                <div className="font-semibold text-lg text-gray-800">
                  {compra.proveedor_info?.nombre || compra.proveedor || 'Sin especificar'}
                </div>
              </div>
              
              {compra.proveedor_info?.contacto && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Persona de Contacto</div>
                  <div className="flex items-center text-sm">
                    <FaUserTie className="text-gray-500 mr-2" />
                    {compra.proveedor_info.contacto}
                  </div>
                </div>
              )}
              
              {compra.proveedor_info?.telefono && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Teléfono</div>
                  <div className="flex items-center text-sm">
                    <FaPhone className="text-gray-500 mr-2" />
                    {compra.proveedor_info.telefono}
                  </div>
                </div>
              )}
              
              {compra.proveedor_info?.email && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Email</div>
                  <div className="flex items-center text-sm">
                    <FaEnvelope className="text-gray-500 mr-2" />
                    {compra.proveedor_info.email}
                  </div>
                </div>
              )}
              
              {compra.proveedor_info?.direccion && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Dirección</div>
                  <div className="flex items-center text-sm">
                    <FaMapMarkerAlt className="text-gray-500 mr-2" />
                    {compra.proveedor_info.direccion}
                  </div>
                </div>
              )}
            </div>
          </Card>
          
          <Card
            title="Resumen"
            icon={<FaFileAlt />}
          >
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span>{veCostos ? `$${formatearNumero(compra.subtotal)}` : 'Reservado'}</span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Impuestos:</span>
                <span>{muestraImporte ? `$${formatearNumero(compra.impuestos)}` : 'Reservado'}</span>
              </div>
              
              <div className="flex justify-between font-medium text-lg border-t pt-2">
                <span>Total:</span>
                <span className="text-indigo-600">{muestraImporte ? `$${formatearNumero(compra.total)}` : 'Reservado'}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
      
      {/* Diálogo de confirmación */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={
          accion === 'recibir' ? 'Recibir Compra' : 
          accion === 'cancelar' ? 'Cancelar Compra' : 
          accion === 'devolver' ? 'Devolver Compra' : 'Confirmar Acción'
        }
        message={
          accion === 'recibir'
            ? `¿Estás seguro de marcar la compra ${referenciaCompra(compra)} como recibida? Esta acción actualizará el inventario.`
            : accion === 'cancelar'
            ? `¿Estás seguro de cancelar la compra ${referenciaCompra(compra)}? Esta acción no se puede deshacer.`
            : accion === 'devolver'
            ? `¿Estás seguro de devolver la compra ${referenciaCompra(compra)}? Esta acción restará el inventario.`
            : '¿Estás seguro de realizar esta acción?'
        }
        confirmText={
          accion === 'recibir' ? 'Recibir' : 
          accion === 'cancelar' ? 'Cancelar' : 
          accion === 'devolver' ? 'Devolver' : 'Confirmar'
        }
        cancelText="Volver"
        onConfirm={confirmarAccion}
        onCancel={cancelarAccion}
      />

      <CompraCostosCambioModal
        open={costosModal.open}
        cambios={costosModal.cambios}
        applying={costosModal.applying}
        error={costosModal.error}
        tipoRedondeo={costosModal.tipoRedondeo}
        onClose={() => setCostosModal({ open: false, cambios: [], applying: false, error: '', tipoRedondeo: 'sin_redondeo', resultado: null, alineadas: [], bloqueadas: [], incidente: null })}
        onConfirm={aplicarDecisiones}
        onRedondeo={cambiarRedondeo}
        onCerrarSinCambios={cerrarSinCambios}
        onRecuperar={recuperarRevision}
        resultado={costosModal.resultado}
        alineadas={costosModal.alineadas}
        bloqueadas={costosModal.bloqueadas}
        incidente={costosModal.incidente}
      />
    </div>
  );
};

export default DetalleCompra;
